import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "../../styles/emailBodyEditor.css";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";

type InputMode = "manual_input" | "job_applications";

interface CareerJob {
  id: number;
  job_title: string;
}

interface StatusOption {
  status: string;
}

export default function EmailBodyEditor() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { toCaptionCase } = useFormat();

  const navigate = useNavigate();
  const { id } = useParams();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");

  const [inputMode, setInputMode] = useState<InputMode>("manual_input");
  const [jobId, setJobId] = useState("");
  const [applicationStatus, setApplicationStatus] = useState("");

  const [jobs, setJobs] = useState<CareerJob[]>([]);
  const [jobStatuses, setJobStatuses] = useState<StatusOption[]>([]);

  useEffect(() => {
    if (!editorRef.current || quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          [{ align: [] }],
          ["clean"],
        ],
      },
    });
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/admin/email-settings/career-jobs-for-email-bodies`,
          {
            credentials: "include",
          },
        );
        const result = await res.json();

        if (result.success) {
          setJobs(result.data || []);
        }
      } catch (error) {
        console.error("Fetch Career Jobs Error:", error);
      }
    };

    fetchJobs();
  }, [BASE_URL]);

  useEffect(() => {
    if (!jobId) {
      setJobStatuses([]);
      setApplicationStatus("");
      return;
    }

    const fetchStatuses = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/admin/email-settings/career-application-statuses/${jobId}`,
          {
            credentials: "include",
          },
        );
        const result = await res.json();

        if (result.success) {
          setJobStatuses(result.data || []);
        }
      } catch (error) {
        console.error("Fetch Career Application Statuses Error:", error);
      }
    };

    fetchStatuses();
  }, [BASE_URL, jobId]);

  useEffect(() => {
    if (!id) return;

    const fetchEmailBody = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/admin/email-settings/email-bodies/${id}`,
          {
            credentials: "include",
          },
        );

        const result = await res.json();

        if (result.success) {
          setName(result.data.name || "");
          setStatus(result.data.status || "active");
          setInputMode(result.data.input_mode || "manual_input");
          setJobId(result.data.job_id ? String(result.data.job_id) : "");
          setApplicationStatus(result.data.application_status || "");

          if (quillRef.current) {
            let editorBody = result.data.body || "";

            if (
              (result.data.input_mode || "manual_input") === "job_applications"
            ) {
              editorBody = editorBody.replace(
                /^\s*<p>\s*Dear\s*\$\{full_name\},\s*<\/p>/i,
                "",
              );
            }

            quillRef.current.root.innerHTML = editorBody;
          }
        }
      } catch (error) {
        console.error("Fetch Email Body Error:", error);
      }
    };

    fetchEmailBody();
  }, [BASE_URL, id]);

  const selectedJobTitle =
    jobs.find((job) => String(job.id) === String(jobId))?.job_title || "";

  const handleSave = async () => {
    let html = quillRef.current?.root.innerHTML || "";

    const computedName =
      inputMode === "job_applications"
        ? [selectedJobTitle, applicationStatus].filter(Boolean).join(" - ")
        : name;

    if (inputMode === "manual_input" && !name.trim()) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Body Name is required.",
        confirmText: "OK",
      });
      return;
    }

    if (inputMode === "job_applications" && !jobId) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please select a Job Title.",
        confirmText: "OK",
      });
      return;
    }

    if (inputMode === "job_applications" && !applicationStatus) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please select an Application Status.",
        confirmText: "OK",
      });
      return;
    }

    if (inputMode === "job_applications") {
      html = html.replace(/^\s*<p>\s*Dear\s*\$\{full_name\},\s*<\/p>/i, "");
    }

    const finalBody =
      inputMode === "job_applications"
        ? `<p>Dear \${full_name},</p>${html}`
        : html;

    try {
      const method = id ? "PUT" : "POST";

      const url = id
        ? `${BASE_URL}/admin/email-settings/email-bodies/${id}`
        : `${BASE_URL}/admin/email-settings/email-bodies`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: computedName,
          body: finalBody,
          status,
          input_mode: inputMode,
          job_id: inputMode === "job_applications" ? Number(jobId) : null,
          application_status:
            inputMode === "job_applications" ? applicationStatus : null,
        }),
      });

      const result = await res.json();

      if (result.success) {
        showModal({
          type: "success",
          title: "Success",
          message: id
            ? "Email body updated successfully."
            : "Email body created successfully.",
          confirmText: "OK",
          onConfirm: () => navigate("/admin/email-bodies"),
        });
      }
    } catch (error) {
      console.error("Save Email Body Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to save email body.",
        confirmText: "OK",
      });
    }
  };

  return (
    <div className="email-editor-page">
      <h2>{id ? "Edit Email Body" : "Create Email Body"}</h2>

      <div className="editor-form">
        <div className="form-group">
          <label>Email Type</label>

          <div className="radio-group">
            <label>
              <input
                type="radio"
                checked={inputMode === "manual_input"}
                onChange={() => setInputMode("manual_input")}
              />
              Manual Input
            </label>

            <label>
              <input
                type="radio"
                checked={inputMode === "job_applications"}
                onChange={() => setInputMode("job_applications")}
              />
              Job Applications
            </label>
          </div>
        </div>

        {inputMode === "manual_input" ? (
          <div className="form-group">
            <label>Body Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Example: Order Confirmation Email"
            />
          </div>
        ) : (
          <div
            className="form-group"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label>Job Title</label>
              <select
                value={jobId}
                onChange={(e) => {
                  setJobId(e.target.value);
                  setApplicationStatus("");
                }}
              >
                <option value="">Select Job Title</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.job_title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Application Status</label>
              <select
                value={applicationStatus}
                onChange={(e) => setApplicationStatus(e.target.value)}
                disabled={!jobId}
              >
                <option value="">Select Status</option>
                {jobStatuses.map((item, index) => (
                  <option key={`${item.status}-${index}`} value={item.status}>
                    {toCaptionCase(item.status)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {inputMode === "job_applications" && (
          <div className="form-group">
            <label>Auto Greeting</label>
            <input value="Dear ${full_name}," disabled />
          </div>
        )}

        <div className="form-group">
          <label>Email Body</label>
          <div
            ref={editorRef}
            style={{
              height: "400px",
              background: "#fff",
              marginBottom: "30px",
            }}
          />
        </div>

        <div className="form-group">
          <label>Status</label>

          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="active"
                checked={status === "active"}
                onChange={() => setStatus("active")}
              />
              Active
            </label>

            <label>
              <input
                type="radio"
                value="inactive"
                checked={status === "inactive"}
                onChange={() => setStatus("inactive")}
              />
              Inactive
            </label>
          </div>
        </div>

        <div className="editor-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate("/admin/email-bodies")}
          >
            Cancel
          </button>

          <button className="btn-primary" onClick={handleSave}>
            Save Email Body
          </button>
        </div>
      </div>
    </div>
  );
}
